import { Utils, StringUtils, ArrayUtils, Logger, engineComponent } from "neo-common";
import { ShapeElement } from "./ShapeElement";
import { ComponentTypes } from "../../ComponentTypes";
import { StageController } from "../../StageController";
import { Tweens, TweensAttributes } from "../../Effects/Tweens";
import { IElement } from "../Interfaces/IElement";
import { Attributes } from "../../../Utils/Attributes";

/**
 * BaseElement class is class which creates holder for other display objects.
 * Also handle all basic properties of display object as X, Y, Rotation, Tween, Scale, Visible, Mask.
 * @class BaseElement
 * @extends PIXI.Container
 */
@engineComponent(ComponentTypes.DisplayObject)
export class BaseElement extends PIXI.Container implements IElement {
    /**
     * Unique component identifier
     */
    private _uid: string;
    private _cloned: boolean;
    private __destroyed: boolean;

    maskSprite: PIXI.Sprite;
    maskGraphics: ShapeElement;

    static _destroyOptions: PIXI.DestroyOptions = {
        children: true,
    };

    public scaleTween: Array<PIXI.Point>;
    public rotationTween: Array<number>;
    public alphaTween: Array<number>;

    constructor() {
        super();

        this._cloned = false;
        this.__destroyed = false;

        this._uid = `${this.componentName}_${Utils.generateUuid()}`;

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                this._uid = PerfDebug.generateElementUID(this);
                PerfDebug.newElement(this, this.getDebugInfo());
            }
        }
    }

    getDebugInfo(): string {
        if (DEBUG) {
            let debugInfoFn = (this as any).debugInfo;
            return debugInfoFn ? debugInfoFn.call(this) : `${this.uid}\nName: [${this.name}]`;
        }
        return "";
    }

    /**
     * @property cloned - indicates if element was cloned or not.
     * @returns {boolean}
     */
    public get cloned(): boolean {
        return this._cloned;
    }

    public get destroyed(): boolean {
        return this.__destroyed;
    }

    /**
     * @property uid - unique identifier of element.
     * @returns {string}
     */
    public get uid(): string {
        return this._uid;
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public get componentName(): string {
        return (this as any).__componentName__;
    }

    destroy(options?: PIXI.DestroyOptions | boolean): void {
        this.assertDestroyed("destroy");

        if (Utils.isNullOrUndefined(options)) {
            options = BaseElement._destroyOptions;
        }

        this.internalDestroy(options);

        if (!Utils.isNullOrUndefined(this.children)) {
            super.destroy(options);
        }

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.destroy(this);
            }
        }

        this.__destroyed = true;
    }

    /**
     * todo Destroy tweens by used tween engine
     * @method internalDestroy - cleanup resources
     * @param options
     */
    protected internalDestroy(options?: PIXI.DestroyOptions | boolean): void {
        Tweens.removeTweens(this);
    }

    public clone(): any {
        throw new Error("Not implemented");
    }

    /**
     * @method removeAndDestroyChildren - recursivelly destroy and remove whole children tree.
     */
    protected removeAndDestroyChildren(): void {
        this._removeAndDestroyChildren();
    }

    /**
     * @method removeAndDestroyChildren - recursivelly destroy and remove whole children tree.
     */
    _removeAndDestroyChildren(): void {
        this.assertDestroyed("_removeAndDestroyChildren");

        let destroyOptions: PIXI.DestroyOptions = {
            children: true,
        };

        if (this.children != null && this.children.length > 0) {
            for (let i = this.children.length - 1; i >= 0; i--) {
                if (this.children[i] != null) {
                    (this.children[i] as any).destroy(destroyOptions);
                }
            }
        }

        this.removeChildren();
    }

    protected assertDestroyed(method: string): void {
        if (DEBUG) {
            if (this.__destroyed) {
                throw new Error(`${this.componentName}[${this.uid}] was already destroyed, calling method '${method}'`);
            }
        }
    }

    /**
     * Marks element as cloned
     */
    protected markAsCloned(): void {
        this._markAsCloned();
    }

    /**
     * Marks element as cloned
     */
    _markAsCloned(): void {
        this.assertDestroyed("markAsCloned()");
        this._cloned = true;

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.clone(this);

                PerfDebug.updateDebugInfo(this, this.getDebugInfo());
            }
        }
    }

    public create(node: Element): void {
        this.createByNode(node);
        this.updateByNode(node);
    }

    /**
     *
     * @method _create - setup element from xml node
     * @param node
     * @private
     */
    public createByNode(node: Element): void {
        if (DEBUG) {
            if (
                node.hasAttribute(BaseElementAttributes.DEBUG) &&
                node.getAttribute(BaseElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        this.assertDestroyed("_create()");

        if (this.name == null) {
            this.name = node.nodeName;
        }

        this.alpha = Attributes.assignFloatAttribute(node, BaseElementAttributes.ALPHA, this.alpha);

        if (node.hasAttribute(BaseElementAttributes.ROTATION)) {
            this.rotation = this.degrees2radians(parseFloat(node.getAttribute(BaseElementAttributes.ROTATION)));
        }

        if (node.hasAttribute(BaseElementAttributes.START_ROTATION)) {
            if (node.getAttribute(BaseElementAttributes.START_ROTATION) === BaseElementAttributes.START_ROTATION) {
                //this.rotation = Math.floor(Math.random() * 360);
                this.rotation = Math.floor(Math.random() * 360) / 100;
            }
        }

        /* todo Move scale.x && scale.y into SCALE attribute condition ? */
        this.scale.x = Attributes.assignFloatAttribute(node, BaseElementAttributes.SCALE_X, this.scale.x);
        this.scale.y = Attributes.assignFloatAttribute(node, BaseElementAttributes.SCALE_Y, this.scale.y);

        if (node.hasAttribute(BaseElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(BaseElementAttributes.SCALE));
            this.scale.y = parseFloat(node.getAttribute(BaseElementAttributes.SCALE));
        }

        this.position.x = Attributes.assignIntAttribute(node, BaseElementAttributes.X, this.position.x);
        this.position.y = Attributes.assignIntAttribute(node, BaseElementAttributes.Y, this.position.y);

        this.pivot.x = Attributes.assignIntAttribute(node, BaseElementAttributes.PIVOT_X, this.pivot.x);
        this.pivot.y = Attributes.assignIntAttribute(node, BaseElementAttributes.PIVOT_Y, this.pivot.y);
        this.visible = Attributes.assignBooleanAttribute(node, BaseElementAttributes.VISIBLE, this.visible);

        if (node.hasAttribute(BaseElementAttributes.MASK)) {
            this.maskGraphics = new ShapeElement();
            let shapeNode: Element = document.createElement(node.getAttribute(BaseElementAttributes.MASK));
            shapeNode.setAttribute("FillAlpha", "1");
            shapeNode.setAttribute("Color", "FF0000");

            if (node.hasAttribute(BaseElementAttributes.MASK_DRAW)) {
                shapeNode.setAttribute("Draw", node.getAttribute(BaseElementAttributes.MASK_DRAW));
            }
            if (node.hasAttribute(BaseElementAttributes.MASK_X)) {
                shapeNode.setAttribute("X", node.getAttribute(BaseElementAttributes.MASK_X));
            }
            if (node.hasAttribute(BaseElementAttributes.MASK_Y)) {
                shapeNode.setAttribute("Y", node.getAttribute(BaseElementAttributes.MASK_Y));
            }
            if (node.hasAttribute(BaseElementAttributes.MASK_WIDTH)) {
                shapeNode.setAttribute("Width", node.getAttribute(BaseElementAttributes.MASK_WIDTH));
            }
            if (node.hasAttribute(BaseElementAttributes.MASK_HEIGHT)) {
                shapeNode.setAttribute("Height", node.getAttribute(BaseElementAttributes.MASK_HEIGHT));
            }
            if (node.hasAttribute(BaseElementAttributes.MASK_PATH)) {
                shapeNode.setAttribute("Path", node.getAttribute(BaseElementAttributes.MASK_PATH));
            }

            this.maskGraphics.create(shapeNode);
            this.mask = this.maskGraphics;
            this.addChild(this.mask);

            if (this.mask == null) {
                if (DEBUG) {
                    this.log("ERROR no element with name: " + node.getAttribute(BaseElementAttributes.MASK));
                }
            }
        }

        if (node.hasAttribute(BaseElementAttributes.BITMAP_MASK)) {
            let image = StageController.getTextureByResourceId(node.getAttribute(BaseElementAttributes.BITMAP_MASK));

            if (image != null) {
                this.maskSprite = new PIXI.Sprite(image);
                if (node.hasAttribute(BaseElementAttributes.BITMAP_MASK_X)) {
                    this.maskSprite.position.x = parseFloat(node.getAttribute(BaseElementAttributes.BITMAP_MASK_X));
                }
                if (node.hasAttribute(BaseElementAttributes.BITMAP_MASK_Y)) {
                    this.maskSprite.position.y = parseFloat(node.getAttribute(BaseElementAttributes.BITMAP_MASK_Y));
                }

                if (node.hasAttribute("BitmapMaskPivotX")) {
                    this.maskSprite.pivot.x = parseInt(node.getAttribute("BitmapMaskPivotX"));
                }

                if (node.hasAttribute("BitmapMaskPivotY")) {
                    this.maskSprite.pivot.y = parseInt(node.getAttribute("BitmapMaskPivotY"));
                }

                if (node.hasAttribute("BitmapMaskScaleY")) {
                    this.maskSprite.scale.y = parseFloat(node.getAttribute("BitmapMaskScaleY"));
                }

                if (node.hasAttribute("BitmapMaskScaleX")) {
                    this.maskSprite.scale.x = parseFloat(node.getAttribute("BitmapMaskScaleX"));
                }
                if (!node.hasAttribute(BaseElementAttributes.BITMAP_MASK_SHOW)) {
                    this.addChild(this.maskSprite);
                    this.mask = this.maskSprite;
                }
            } else {
                if (DEBUG) {
                    this.log(
                        "ERROR" +
                            this.componentName +
                            " no image with name for mask: " +
                            node.getAttribute(BaseElementAttributes.BITMAP_MASK),
                    );
                }
            }
        }

        if (this.maskGraphics != null) {
            if (node.hasAttribute("MaskScaleX")) {
                this.maskGraphics.scale.x = parseFloat(node.getAttribute("MaskScaleX"));
            }

            if (node.hasAttribute("MaskScaleY")) {
                this.maskGraphics.scale.y = parseFloat(node.getAttribute("MaskScaleY"));
            }

            if (node.hasAttribute("MaskX")) {
                this.maskGraphics.position.x = parseFloat(node.getAttribute("MaskX"));
            }

            if (node.hasAttribute("MaskY")) {
                this.maskGraphics.position.y = parseFloat(node.getAttribute("MaskY"));
            }
        }

        // NOTE(psulek) Do not join these two ifs into one, required 2 ifs for webpack optimization
        if (DEBUG) {
            if (PERFDEBUG_ENABLED) {
                PerfDebug.createElement(this, this.getDebugInfo());
            }
        }
    }

    /**
     * @param node
     */
    public updateByNode(node: Element): void {
        if (!node) {
            this.logError("No node specified for element update!");
            return;
        }

        if (DEBUG) {
            if (
                node.hasAttribute(BaseElementAttributes.DEBUG) &&
                node.getAttribute(BaseElementAttributes.DEBUG) === "true"
            ) {
                debugger;
            }
        }

        if (
            !node.hasAttribute("RemoveTweens") ||
            StringUtils.parseBoolean(node.getAttribute("RemoveTweens")) != false
        ) {
            Tweens.removeTweens(this);
        }

        this.visible = Attributes.assignBooleanAttribute(node, BaseElementAttributes.VISIBLE, this.visible);
        this.alpha = Attributes.assignFloatAttribute(node, BaseElementAttributes.ALPHA, this.alpha);
        this.pivot.x = Attributes.assignIntAttribute(node, BaseElementAttributes.PIVOT_X, this.pivot.x);
        this.pivot.y = Attributes.assignIntAttribute(node, BaseElementAttributes.PIVOT_Y, this.pivot.y);

        if (node.hasAttribute(BaseElementAttributes.ROTATION)) {
            this.rotation = this.degrees2radians(parseFloat(node.getAttribute(BaseElementAttributes.ROTATION)));
        }

        if (node.hasAttribute(BaseElementAttributes.START_ROTATION)) {
            if (node.getAttribute(BaseElementAttributes.START_ROTATION) === BaseElementAttributes.START_ROTATION) {
                this.rotation = Math.floor(Math.random() * 360) / 100;
            }
        }

        this.position.x = Attributes.assignIntAttribute(node, BaseElementAttributes.X, this.position.x);
        this.position.y = Attributes.assignIntAttribute(node, BaseElementAttributes.Y, this.position.y);

        if (node.hasAttribute(BaseElementAttributes.SCALE_X)) {
            this.scale.x = parseFloat(node.getAttribute(BaseElementAttributes.SCALE_X));
        }

        if (node.hasAttribute(BaseElementAttributes.SCALE_Y)) {
            this.scale.y = parseFloat(node.getAttribute(BaseElementAttributes.SCALE_Y));
        }

        if (node.hasAttribute(BaseElementAttributes.SCALE)) {
            this.scale.x = parseFloat(node.getAttribute(BaseElementAttributes.SCALE));
            this.scale.y = parseFloat(node.getAttribute(BaseElementAttributes.SCALE));
        }

        this.runAlphaTween(node);
        this.runPositionTween(node);
        this.runRotationTween(node);
        this.runScaleTween(node);
        this.runTween(node);
    }

    /**
     * Runs the alpha tween
     * @method runAlphaTween
     * @param node
     * @returns {boolean}
     */
    public runAlphaTween(node: Element): boolean {
        if (!node.hasAttribute(BaseElementAttributes.ALPHA_TWEEN)) {
            return false;
        }

        let alphaTween: Array<number> = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.ALPHA_TWEEN));

        const alphas: Array<number> = alphaTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(node, BaseElementAttributes.ALPHA_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.ALPHA_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, BaseElementAttributes.ALPHA_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            BaseElementAttributes.ALPHA_TWEEN_LOOP,
            null,
        );

        Tweens.alphaTween(this, { alphas, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     * Runs the position tween
     * @method runPositionTween
     * @param node
     * @returns {boolean}
     */
    public runPositionTween(node: Element): boolean {
        if (
            !node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_X) ||
            !node.hasAttribute(BaseElementAttributes.POSITION_TWEEN_Y)
        ) {
            return false;
        }

        let positionTweenX: Array<number>;
        let positionTweenY: Array<number>;

        let positionTweenPosition: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.POSITION_TWEEN_POSITION,
            null,
        );
        if (positionTweenPosition == BaseElementAttributes.POSITION_TWEEN_POSITION_VALUE.RELATIVE) {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_X)).map(
                (value) => value + this.position.x,
            );
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_Y)).map(
                (value) => value + this.position.y,
            );
        } else {
            positionTweenX = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_X));
            positionTweenY = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.POSITION_TWEEN_Y));
        }

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.POSITION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.POSITION_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, BaseElementAttributes.POSITION_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            BaseElementAttributes.POSITION_TWEEN_LOOP,
            null,
        );

        Tweens.positionTween(this, { tweenX, tweenY, tweenEase, tweenDuration, tweenDelay, tweenLoop }); // , callbackFunction: () => {this.emit('positionTweenFinished')}
    }

    /**
     * Runs the rotation tween
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runRotationTween(node: Element): boolean {
        if (!node.hasAttribute(BaseElementAttributes.ROTATION_TWEEN)) {
            return false;
        }

        let rotationTween: Array<number> = ArrayUtils.numberArray(
            node.getAttribute(BaseElementAttributes.ROTATION_TWEEN),
        );

        const degrees: Array<number> = rotationTween || [];

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            BaseElementAttributes.ROTATION_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.ROTATION_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, BaseElementAttributes.ROTATION_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            BaseElementAttributes.ROTATION_TWEEN_LOOP,
            null,
        );

        Tweens.rotationTween(this, { degrees, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     * Runs the scale tween
     *
     * @method runScaleTween
     * @param node
     * @returns {boolean}
     */
    public runScaleTween(node: Element): boolean {
        let scaleTweenX: Array<number>;
        let scaleTweenY: Array<number>;
        let scaleTween: Array<PIXI.Point>;
        let tweenValues: Array<PIXI.Point> = [];

        if (node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_X)) {
            scaleTweenX = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SCALE_TWEEN_X));
        }

        if (node.hasAttribute(BaseElementAttributes.SCALE_TWEEN_Y)) {
            scaleTweenY = ArrayUtils.numberArray(node.getAttribute(BaseElementAttributes.SCALE_TWEEN_Y));
        }

        if (node.hasAttribute(BaseElementAttributes.SCALE_TWEEN)) {
            scaleTween = BaseElement._getPointsFromPointsAttribute(
                node.getAttribute(BaseElementAttributes.SCALE_TWEEN),
            ) as Array<PIXI.Point>;
        }

        if (scaleTween && scaleTween.length) {
            tweenValues = scaleTween;
        } else if (scaleTweenX && scaleTweenY) {
            if (scaleTweenX.length === scaleTweenY.length) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: scaleTweenY[i],
                    } as PIXI.Point;
                });
            }
        } else if (scaleTweenX) {
            tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                return {
                    x: scaleXValue,
                    y: this.scale.y,
                } as PIXI.Point;
            });
        } else if (scaleTweenY) {
            tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                return {
                    x: this.scale.x,
                    y: scaleYValue,
                } as PIXI.Point;
            });
        } else {
            return false;
        }

        const tweenEase: string = Attributes.assignStringAttribute(node, BaseElementAttributes.SCALE_TWEEN_EASE, null);
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            BaseElementAttributes.SCALE_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(node, BaseElementAttributes.SCALE_TWEEN_DELAY, 0);
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            BaseElementAttributes.SCALE_TWEEN_LOOP,
            null,
        );

        Tweens.scaleTween(this, { tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    public runTween(node: Element): boolean {
        if (node.hasAttribute(TweensAttributes.TWEEN)) {
            let tweens: Array<string> = ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN));

            for (let i: number = 0; i < tweens.length; i++) {
                //return if there are no values to tween
                if (!node.hasAttribute(TweensAttributes.TWEEN_VALUES + i)) {
                    return false;
                }

                let tween: string = tweens[i];

                const tweenValues: Array<number> = this.formatTweenValues(
                    tween,
                    ArrayUtils.stringArray(node.getAttribute(TweensAttributes.TWEEN_VALUES + i)),
                );
                const tweenDuration: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DURATION + i) || "120",
                );
                const tweenEase: Array<string> = ArrayUtils.stringArray(
                    node.getAttribute(TweensAttributes.TWEEN_EASE + i) || "linear",
                );
                const tweenDelay: Array<number> = ArrayUtils.numberArray(
                    node.getAttribute(TweensAttributes.TWEEN_DELAY + i) || "0",
                );
                const tweenLoop: boolean = Attributes.assignBooleanAttribute(
                    node,
                    TweensAttributes.TWEEN_LOOP + i,
                    null,
                );

                if (tween == "scale") {
                    tween = "scale.x";
                    tweens[i] = "scale.y";
                    i--;
                }

                Tweens.customTween(this, { tween, tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
            }
        }

        return false;
    }

    private formatTweenValues(tween: string, values: Array<string>): Array<number> {
        let tweenValues: Array<number> = [];

        if (tween === "scale") {
            tween = "scale.x";
        }
        let property: Array<string> = tween.split(".");
        let value: number;

        for (let i: number = 0; i < values.length; i++) {
            let current = values[i];
            switch (current[current.length - 1]) {
                case "c": //if we want current value
                    if (property.length == 1) {
                        value = this[property[0]];
                        if (property[0] === "rotation") {
                            value = this.radians2degrees(value);
                        }
                        tweenValues.push(value);
                    } else if (property.length == 2) {
                        tweenValues.push(this[property[0]][property[1]]);
                    }

                    break;
                case "+":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] + value;
                    }
                    tweenValues.push(value);
                    break;
                case "-":
                    value = parseFloat(current.slice(0, -1));
                    if (tweenValues.length > 0) {
                        value = tweenValues[tweenValues.length - 1] - value;
                    }
                    tweenValues.push(value);
                    break;
                case "~":
                    value = parseFloat(current.slice(0, -1));

                    if (property[0] == "position") {
                        if (property[1] == "x") {
                            value = this.position.x + (value - this.getGlobalPosition().x);
                        } else if (property[1] == "y") {
                            value = this.position.y + (value - this.getGlobalPosition().y);
                        }
                    }

                    tweenValues.push(value);
                    break;
                default:
                    tweenValues.push(parseFloat(current));
            }
        }

        return tweenValues;
    }

    /**
     * Converts string from "points attribute" into array of points. E.g. "(1,6);(5,2);2;(2,4)". Point can be specified as single value for both axex.
     * @method _getPointsFromPointsAttribute
     * @return Array<PIXI.Points>
     */
    protected static _getPointsFromPointsAttribute(pointsString: string): Array<{ x: number; y: number }> {
        if (!pointsString) {
            return [];
        }

        return pointsString
            .replace(/\s/g, "")
            .split(";")
            .map((valuesPair: string) =>
                /\(|\)/.test(valuesPair) ? valuesPair.slice(1, -1).split(",") : [valuesPair, valuesPair],
            )
            .map((valuesPair: Array<string>) => {
                return { x: parseFloat(valuesPair[0]), y: parseFloat(valuesPair[1]) };
            });
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }

    /**
     * Log error into console
     * @param s
     */
    protected logError(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.errorGroup(compName, s);
        }
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public degrees2radians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    public radians2degrees(radians: number): number {
        return radians * (180 / Math.PI);
    }

    public maskEnabled(enable: boolean): void {
        if (enable) {
            if (this.maskGraphics) {
                this.mask = this.maskGraphics;
                this.addChild(this.maskGraphics);
            }
        } else {
            this.removeChild(this.maskGraphics);
            this.mask = null;
        }
    }
}

export const BaseElementAttributes = {
    /*
     * Alpha (transparency) for this display object. 0 is fully transparent, 1 is fully opaque.
     * @property Alpha
     * @type Number
     * @default 1
     */
    ALPHA: "Alpha",

    ALPHA_TWEEN: "AlphaTween",
    ALPHA_TWEEN_EASE: "AlphaTweenEase",
    ALPHA_TWEEN_DELAY: "AlphaTweenDelay",
    ALPHA_TWEEN_DURATION: "AlphaTweenDuration",
    ALPHA_TWEEN_LOOP: "AlphaTweenLoop",
    /*
     * A ImageElement instance that defines a mask for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property BitmapMask
     * @type string
     * @default null
     */
    BITMAP_MASK: "BitmapMask",
    BITMAP_MASK_SHOW: "BitmapMaskShow",
    BITMAP_MASK_X: "BitmapMaskX",
    BITMAP_MASK_Y: "BitmapMaskY",
    DEBUG: "Debug",
    /*
     * A ShapeElement instance that defines a vector mask (clipping path) for this display object.
     * The shape's transformation will be applied relative to the display object's parent coordinates (as if it were a child of the parent).
     * @property Mask
     * @type ShapeElement
     * @default null
     */
    MASK: "Mask",
    MASK_X: "MaskX",
    MASK_Y: "MaskY",
    MASK_WIDTH: "MaskWidth",
    MASK_HEIGHT: "MaskHeight",
    MASK_PATH: "MaskPath",
    MASK_DRAW: "MaskDraw",
    /*
     * X pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotX
     * @type number
     * @default null
     */
    PIVOT_X: "PivotX",
    /*
     * Y pivot for container. Used mainly for scale tweening and rotation.
     * @property PivotY
     * @type number
     * @default null
     */
    PIVOT_Y: "PivotY",
    POSITION_TWEEN_X: "PositionTweenX",
    POSITION_TWEEN_Y: "PositionTweenY",
    POSITION_TWEEN_EASE: "PositionTweenEase",
    POSITION_TWEEN_DURATION: "PositionTweenDuration",
    POSITION_TWEEN_DELAY: "PositionTweenDelay",
    POSITION_TWEEN_POSITION: "PositionTweenPosition",
    POSITION_TWEEN_POSITION_VALUE: {
        ABSOLUTE: "absolute",
        RELATIVE: "relative",
    },
    POSITION_TWEEN_LOOP: "PositionTweenLoop",
    /*
     *
     */
    ROTATION_TWEEN: "RotationTween",
    /**
     * @deprecated Use ROTATION_TWEEN instead ROTATION_TWEEN_DEGREES
     */
    ROTATION_TWEEN_DEGREES: "RotationTweenDegrees",
    ROTATION_TWEEN_DELAY: "RotationTweenDelay",
    ROTATION_TWEEN_DURATION: "RotationTweenDuration",
    ROTATION_TWEEN_EASE: "RotationTweenEase",
    ROTATION_TWEEN_LOOP: "RotationTweenLoop",
    /*
     * Rotation in degrees for this display object.
     * @property Rotation
     * @type Number
     * @default 0
     */
    ROTATION: "Rotation",
    SCALE_TWEEN: "ScaleTween",
    SCALE_TWEEN_DELAY: "ScaleTweenDelay",
    /**
     * @deprecated Use SCALE_TWEEN instead SCALE_TWEEN_DURATION
     */
    SCALE_TWEEN_DURATION: "ScaleTweenDuration",
    SCALE_TWEEN_EASE: "ScaleTweenEase",
    SCALE_TWEEN_LOOP: "ScaleTweenLoop",
    SCALE_TWEEN_X: "ScaleTweenX",
    SCALE_TWEEN_Y: "ScaleTweenY",
    /*
     * The factor to stretch this display object horizontally.
     * For example, setting scaleX to 2 will stretch the display object to twice its nominal width. To horizontally flip an object, set the scale to a negative number.
     * @property ScaleX
     * @type Number
     * @default 1
     */
    /*
     * The factor to stretch this display object horizontally and vertically. Use this instead of using ScaleX and ScaleY with same value.
     * @property Scale
     * @type Number
     * @default 1
     */
    SCALE: "Scale",
    SCALE_X: "ScaleX",
    /*
     * The factor to stretch this display object vertically.
     * For example, setting scaleY to 0.5 will stretch the display object to half its nominal height. To vertically flip an object, set the scale to a negative number.
     * @property ScaleY
     * @type Number
     * @default 1
     */
    SCALE_Y: "ScaleY",
    /*
     * Random rotation in degrees for this display object.
     * @property StartRotation
     * @type Number
     * @default null
     */
    START_ROTATION: "StartRotation",
    /*
     * Indicates whether this display object should be rendered to the canvas and included when running the Stage Stage/getObjectsUnderPoint method.
     * @property Visible
     * @type Boolean
     * @default true
     */
    VISIBLE: "Visible",
    /*
     * X (horizontal) position of the display object, relative to its parent.
     * @property X
     * @type Number
     * @default 0
     */
    X: "X",
    /*
     * Y (vertical) position of the display object, relative to its parent.
     * @property Y
     * @type Number
     * @default 0
     */
    Y: "Y",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Elements/Graphics/BaseElement.ts
 **/